import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { Lang } from '@core/types';
import { mt, recorder, tts, whisper } from './engines/singletons';
import { setOfflineMode, withDownloadsAllowed } from './transformersEnv';
import { MAX_RECORDING_MS } from './audio';
import {
  getLastWarmUpMs,
  getSttDevice,
  getSttVariant,
  setLastWarmUpMs,
} from './settings';
import { logEvent } from './eventLog';
import { createDownloadProgress, formatDownload, type DownloadProgress } from './loadProgress';

/**
 * Above this, a one-second clip is taking long enough that real sentences will
 * be unusable, and the honest move is to offer a smaller model rather than let
 * someone discover it in a train station.
 */
const SLOW_WARMUP_MS = 8000;

/**
 * Hold-to-talk gesture thresholds, in pixels of horizontal travel.
 *
 * Two directions, borrowed from the messaging apps everyone already has muscle
 * memory for: drag right past LOCK_PX and recording continues hands-free, drag
 * left past CANCEL_PX and it is thrown away. Cancel is the shorter throw
 * because it is the one you reach for in a hurry, and because getting it wrong
 * costs nothing — you just say it again.
 */
const LOCK_PX = 72;
const CANCEL_PX = 88;

/**
 * Below this, a press was a tap and not an attempt to say something. The old
 * button had no such floor, which is most of why it felt unreliable: a brush
 * registered as a recording, and the feedback for that was a rejection notice
 * several seconds later rather than an immediate "hold it down".
 */
const MIN_RECORDING_MS = 350;

type InputMode = 'voice' | 'text';
type OutputMode = 'voice' | 'text';
type Phase = 'idle' | 'recording' | 'thinking' | 'ready' | 'error';

const LABEL: Record<Lang, string> = { en: 'English', ru: 'Русский' };
const FLAG: Record<Lang, string> = { en: '🇬🇧', ru: '🇷🇺' };

interface Turn {
  source: string;
  translation: string;
  backTranslation: string | null;
  from: Lang;
  to: Lang;
  ms: number;
}

export default function Translator({ onOpenDiagnostics }: { onOpenDiagnostics: () => void }) {
  const reduce = useReducedMotion();

  const [from, setFrom] = useState<Lang>('en');
  const to: Lang = from === 'en' ? 'ru' : 'en';

  const [inputMode, setInputMode] = useState<InputMode>('voice');
  const [outputMode, setOutputMode] = useState<OutputMode>('voice');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [turn, setTurn] = useState<Turn | null>(null);
  const [speakError, setSpeakError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [elapsed, setElapsed] = useState(0);

  // Two readiness flags, not one. Typing needs only the translation model,
  // which loads in seconds; speech needs Whisper, which does not. Gating the
  // whole screen on the slowest of them is what produced a four-minute wait.
  const [mtReady, setMtReady] = useState(false);
  const [sttReady, setSttReady] = useState(false);
  // Aggregate progress, not the per-file number transformers.js reports — see
  // `loadProgress.ts` for why the raw one reads as though it is going backwards.
  const [mtProgress, setMtProgress] = useState<DownloadProgress | null>(null);
  const [sttProgress, setSttProgress] = useState<DownloadProgress | null>(null);
  const [warmUpMs, setWarmUpMs] = useState<number | null>(getLastWarmUpMs());

  /** Hands-free recording: the finger has left the button but capture continues. */
  const [locked, setLocked] = useState(false);
  /** Horizontal travel of the current drag, in pixels. Positive is toward lock. */
  const [dragX, setDragX] = useState(0);

  const recordingRef = useRef(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef(0);
  const dragOriginRef = useRef(0);

  /* ------------------------------------------------------------ model setup */

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Stage 1 — the active direction only. Enough to type and translate.
        const trackMt = createDownloadProgress();
        await withDownloadsAllowed(() =>
          mt.load(from, to, (p) => !cancelled && setMtProgress(trackMt(p)))
        );
        if (cancelled) return;
        setMtProgress(null);
        setMtReady(true);

        tts.initialize().catch(() => undefined);

        // Stage 2 — speech, in the background. The screen is already usable.
        const device = getSttDevice();
        const variant = getSttVariant();
        const trackStt = createDownloadProgress();
        await withDownloadsAllowed(() =>
          whisper.load(variant, device, (p) => !cancelled && setSttProgress(trackStt(p)))
        );
        if (cancelled) return;
        setSttProgress(null);
        setSttReady(true);

        // The reverse direction is NOT loaded here. It is another ~75 MB, and
        // on a slow connection it doubled the wait for something only needed
        // after the user switches direction. It loads on demand instead.
        //
        // Everything needed to translate is present, so close the door: from
        // here a missing file is an error, never a quiet fetch.
        setOfflineMode(true);

        // Stage 3 — warm-up last, and deliberately not awaited by anything the
        // user is waiting on. It pays the one-off shader/kernel compilation so
        // the first real utterance is not the slowest, but blocking startup on
        // it bought nothing and cost minutes.
        whisper
          .warmUp('en')
          .then((ms) => {
            if (cancelled) return;
            setWarmUpMs(ms);
            setLastWarmUpMs(ms);
          })
          .catch(() => undefined);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? String(e));
      }
    })();

    return () => {
      cancelled = true;
    };
    // Runs once. Switching direction reuses already-loaded pipelines.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------------------------------------- the pipeline */

  const translateText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        setError('Nothing to translate.');
        setPhase('error');
        return;
      }
      setPhase('thinking');
      setError(null);
      const started = performance.now();
      try {
        // The forward direction is always loaded; the reverse may still be
        // arriving, so the meaning check degrades rather than blocking.
        const forward = await mt.translate(trimmed, from, to);
        let back: string | null = null;
        if (mt.isLoaded(to, from)) {
          back = (await mt.translate(forward.text, to, from).catch(() => null))?.text ?? null;
        }

        setTurn({
          source: trimmed,
          translation: forward.text,
          backTranslation: back,
          from,
          to,
          ms: performance.now() - started,
        });
        setPhase('ready');
        setSpeakError(null);

        if (outputMode === 'voice') {
          // Kept apart from `error`: a failure to speak must not hide the
          // translation. The text is the thing you can still hold up to
          // someone; the audio is a convenience on top of it.
          tts.speak(forward.text, to).catch((e: any) =>
            setSpeakError(`Couldn't speak it, ${e?.message ?? e}`)
          );
        }
      } catch (e: any) {
        setError(e?.message ?? String(e));
        setPhase('error');
      }
    },
    [from, to, outputMode]
  );

  const startRecording = useCallback(async () => {
    if (!sttReady || recordingRef.current) return;
    recordingRef.current = true;
    startedAtRef.current = performance.now();
    setError(null);
    setTurn(null);
    setLocked(false);
    setDragX(0);
    setPhase('recording');
    setElapsed(0);
    tickRef.current = setInterval(() => setElapsed((e) => e + 100), 100);
    try {
      await recorder.start();
    } catch (e: any) {
      recordingRef.current = false;
      setPhase('error');
      setError(`Microphone unavailable: ${e?.message ?? e}`);
    }
  }, [sttReady]);

  /**
   * Throw the recording away without transcribing.
   *
   * Used by slide-to-cancel and by the too-short guard. Distinct from stopping:
   * there is no point paying thirty seconds of encoder time to find out that a
   * 200 ms accidental brush of the button contained no words.
   */
  const discardRecording = useCallback(async (reason: string | null) => {
    if (!recordingRef.current) return;
    recordingRef.current = false;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    setLocked(false);
    setDragX(0);
    setPhase('idle');
    setError(reason);
    await recorder.cancel().catch(() => undefined);
  }, []);

  const stopAndTranslate = useCallback(async () => {
    if (!recordingRef.current) return;

    // A press shorter than this is a tap, not speech. Before the guard, a
    // stray tap started and stopped a recording, which then either produced a
    // near-silence rejection or — worse — gave Whisper a fragment to invent
    // from. Either way it cost a full inference to say nothing. Treat it as
    // what it was: a mis-tap, answered instantly with an instruction.
    if (performance.now() - startedAtRef.current < MIN_RECORDING_MS) {
      await discardRecording('Hold the button while you speak, or slide right to lock it.');
      return;
    }

    recordingRef.current = false;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    setLocked(false);
    setDragX(0);
    setPhase('thinking');

    try {
      const rec = await recorder.stop();
      const res = await whisper.transcribe(rec.samples, from);

      /**
       * Every transcription is recorded with the conditions that produced it.
       *
       * Speech was reported as good online and poor offline, which no code path
       * explains — the translator only ever calls Whisper, and Whisper never
       * touches the network once loaded. The likelier explanation is that the
       * two runs used different models. Without this line there is no way to
       * tell those apart afterwards, so it goes in the log and the report.
       */
      // The model reported is the one actually in memory, not the stored
      // preference. Those are different things — the preference changes the
      // instant a button is tapped, the loaded pipeline only changes after a
      // reload has re-loaded it — and a log that reports the preference would
      // confidently name the wrong model in precisely the case this line
      // exists to catch. When they disagree, say so rather than picking one.
      const ranVariant = whisper.loadedVariant;
      const preferredVariant = getSttVariant();
      const modelField =
        ranVariant === preferredVariant
          ? `model=${ranVariant}`
          : `model=${ranVariant ?? 'none'} (setting says ${preferredVariant}, MISMATCH)`;

      logEvent(
        `STT ${from} · ${modelField} · ${whisper.activeDevice}/${whisper.activeDtype} · ` +
          `audio=${Math.round(rec.durationMs)}ms · online=${navigator.onLine} · ` +
          `${res.outcome.rejected ? `REJECTED (${res.outcome.rejectReason})` : `"${res.outcome.text}"`} · ` +
          `${Math.round(res.sttMs)}ms`
      );

      if (res.outcome.rejected) {
        setPhase('error');
        // The technical reason still goes to the log, where it is useful. What
        // the person in front of the phone needs is what to do differently.
        setError("Didn't catch that. Try again, a bit closer to the phone.");
        return;
      }
      await translateText(res.outcome.text);
    } catch (e: any) {
      setPhase('error');
      setError(e?.message ?? String(e));
    }
  }, [from, translateText, discardRecording]);

  const replay = useCallback(() => {
    if (!turn) return;
    tts.unlock();
    setSpeakError(null);
    tts.speak(turn.translation, turn.to).catch((e: any) =>
      setSpeakError(`Couldn't speak it, ${e?.message ?? e}`)
    );
  }, [turn]);

  /**
   * Load a direction on demand.
   *
   * Remote loading is re-enabled only for the duration of an explicitly
   * requested download and switched straight back off, so the "no silent
   * fetch" guarantee survives while a genuine one-time download can still
   * happen when someone asks for it.
   */
  const ensureDirection = useCallback(async (f: Lang, t: Lang) => {
    if (mt.isLoaded(f, t)) return;
    const track = createDownloadProgress();
    try {
      await withDownloadsAllowed(() => mt.load(f, t, (p) => setMtProgress(track(p))));
    } finally {
      setMtProgress(null);
    }
  }, []);

  const swap = useCallback(() => {
    const nextFrom: Lang = from === 'en' ? 'ru' : 'en';
    const nextTo: Lang = nextFrom === 'en' ? 'ru' : 'en';
    setFrom(nextFrom);
    setTurn(null);
    setTyped('');
    setError(null);
    setPhase('idle');

    if (!mt.isLoaded(nextFrom, nextTo)) {
      ensureDirection(nextFrom, nextTo).catch((e: any) =>
        setError(
          `${LABEL[nextFrom]} → ${LABEL[nextTo]} needs a one-time download and it did not ` +
            `complete: ${e?.message ?? e}`
        )
      );
    }
  }, [from, ensureDirection]);

  useEffect(() => () => void (tickRef.current && clearInterval(tickRef.current)), []);

  /**
   * The recorder gives the microphone back when the app goes to the background,
   * which abandons any take in progress. Without this the UI would sit on a
   * recording screen, timer still climbing, for audio that no longer exists —
   * and Send would then fail on a recorder that had already let go.
   */
  useEffect(() => {
    recorder.onAbort = () => {
      if (!recordingRef.current) return;
      recordingRef.current = false;
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      setLocked(false);
      setDragX(0);
      setPhase('idle');
      setError('Recording stopped when you left the app. Tap and hold to try again.');
    };
    return () => {
      recorder.onAbort = null;
    };
  }, []);

  /**
   * Hands-free recording has no one holding the button, so nothing tells the
   * user when the recorder hits its own 15-second ceiling and stops. Without
   * this the timer keeps counting into a recording that ended, and Send returns
   * audio that stops well short of what they thought they said. Send it at the
   * limit instead, which is what they would have done had they known.
   */
  useEffect(() => {
    if (locked && elapsed >= MAX_RECORDING_MS) void stopAndTranslate();
  }, [locked, elapsed, stopAndTranslate]);

  const recSeconds = (elapsed / 1000).toFixed(1);
  const nearLimit = elapsed > MAX_RECORDING_MS - 3000;
  /** Slow enough to be worth saying out loud, but there is no faster model to offer. */
  const slowSpeech = warmUpMs !== null && warmUpMs > SLOW_WARMUP_MS;

  /**
   * Everything below is written for whoever picks the phone up, which is not
   * the person who built it. No model names, no backends, no milliseconds. The
   * numbers that matter to them are how long they have to wait and whether it
   * needs signal; anything else has a home in Diagnostics.
   */
  const pill = sttReady
    ? { text: 'Ready', cls: 'ok' }
    : mtReady
    ? { text: 'Ready to type', cls: 'part' }
    : { text: 'Getting ready', cls: '' };

  const idleHint = sttReady
    ? slowSpeech
      ? 'Ready. Speech takes a few seconds on this phone, so give it a moment.'
      : 'Ready. Works without internet.'
    : mtReady
    ? sttProgress !== null
      ? `${formatDownload('Setting up speech', sttProgress)}. You can type while you wait.`
      : 'Setting up speech. You can type while you wait.'
    : mtProgress !== null
    ? formatDownload('Getting things ready', mtProgress)
    : 'Getting things ready.';

  /* ------------------------------------------------------------------- render */

  return (
    <div className="t-root">
      <header className="t-top">
        <motion.span
          className={`t-pill ${pill.cls}`}
          animate={reduce ? undefined : { opacity: sttReady ? 1 : [0.5, 1, 0.5] }}
          transition={{ duration: 1.6, repeat: sttReady ? 0 : Infinity }}
        >
          {pill.text}
        </motion.span>
        {/* The build stamp used to live here, spelled out. It is genuinely
            useful when something is wrong and meaningless the rest of the time,
            so it moved to Diagnostics, where the person who needs it will look
            for it anyway. What is left is one quiet button. */}
        <button className="t-ghost" onClick={onOpenDiagnostics} aria-label="Open settings">
          Settings
        </button>
      </header>

      {/* The banner that used to sit here offered tiny as the answer to slow
          speech. Tiny is gone, and there is no second model worth switching to,
          so a banner here would be a notification with nothing behind it. What
          is left, how slow this phone actually is, belongs in the idle hint,
          which already says it and does not need dismissing. */}

      <button className="t-direction" onClick={swap} aria-label="Swap language direction">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={from}
            className="t-dir-lang"
            initial={reduce ? false : { y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduce ? undefined : { y: -14, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
          >
            {FLAG[from]} {LABEL[from]}
          </motion.span>
        </AnimatePresence>
        <motion.span
          className="t-swap"
          key={`arrow-${from}`}
          initial={reduce ? false : { rotate: -180 }}
          animate={{ rotate: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          aria-hidden="true"
        >
          ⇄
        </motion.span>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={to}
            className="t-dir-lang"
            initial={reduce ? false : { y: -14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduce ? undefined : { y: 14, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
          >
            {FLAG[to]} {LABEL[to]}
          </motion.span>
        </AnimatePresence>
      </button>

      <div className="t-stage">
        <AnimatePresence mode="wait">
          {error && (
            <motion.p
              key="error"
              className="t-error"
              initial={reduce ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {error}
            </motion.p>
          )}

          {!error && phase === 'thinking' && (
            <motion.div
              key="thinking"
              className="t-thinking"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduce ? undefined : { opacity: 0 }}
            >
              <motion.span
                animate={reduce ? undefined : { opacity: [0.35, 1, 0.35] }}
                transition={{ duration: 1.1, repeat: Infinity }}
              >
                Translating…
              </motion.span>
            </motion.div>
          )}

          {!error && phase === 'ready' && turn && (
            <motion.div
              key="result"
              className="t-result"
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -8 }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            >
              <p className="t-source">{turn.source}</p>
              <p className="t-translation" lang={turn.to}>
                {turn.translation}
              </p>
              {turn.backTranslation && (
                <p className="t-back">
                  {/* "Meaning check" meant nothing to anyone who had not been
                      told what it was. Saying what was actually done explains
                      itself and needs no glossary. */}
                  <span className="t-back-label">Translated back, to check</span>
                  {turn.backTranslation}
                </p>
              )}
              {speakError && <p className="t-speak-error">{speakError}</p>}
              <div className="t-result-actions">
                {/* The timing that sat here was for benchmarking, and nobody
                    waiting on a translation wants a stopwatch reading. */}
                <motion.button
                  className="t-speak"
                  onClick={replay}
                  whileTap={reduce ? undefined : { scale: 0.96 }}
                >
                  🔊 Play again
                </motion.button>
              </div>
            </motion.div>
          )}

          {!error && phase === 'idle' && (
            <motion.div
              key="hint"
              className="t-hint-wrap"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduce ? undefined : { opacity: 0 }}
            >
              <p className="t-hint">{idleHint}</p>
              {(mtProgress !== null || sttProgress !== null) && (
                <div className="t-bar" role="progressbar">
                  <motion.div
                    className="t-bar-fill"
                    animate={{
                      width: `${Math.round(sttProgress?.percent ?? mtProgress?.percent ?? 0)}%`,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="t-modes">
        <fieldset className="t-mode-group">
          <legend>Input</legend>
          {(['voice', 'text'] as InputMode[]).map((m) => (
            <button
              key={m}
              className={inputMode === m ? 'sel' : ''}
              onClick={() => setInputMode(m)}
              aria-pressed={inputMode === m}
            >
              {m === 'voice' ? '🎤 Speak' : '⌨️ Type'}
            </button>
          ))}
        </fieldset>
        <fieldset className="t-mode-group">
          <legend>Output</legend>
          {(['voice', 'text'] as OutputMode[]).map((m) => (
            <button
              key={m}
              className={outputMode === m ? 'sel' : ''}
              onClick={() => setOutputMode(m)}
              aria-pressed={outputMode === m}
            >
              {m === 'voice' ? '🔊 Speak' : '🔇 Text only'}
            </button>
          ))}
        </fieldset>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {inputMode === 'voice' ? (
          <motion.div
            key="voice-input"
            className="t-input"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -12 }}
            transition={{ duration: 0.18 }}
          >
            {/* Locked: the finger is gone and capture continues, so the two
                things you might want next become real buttons rather than
                gestures. Discard on the left, send on the right, timer between
                them, nothing here depends on holding still. */}
            {locked ? (
              <div className="t-locked">
                <button
                  className="t-locked-cancel"
                  onClick={() => void discardRecording(null)}
                  aria-label="Discard recording"
                >
                  ✕
                </button>
                <span className={`t-locked-timer ${nearLimit ? 'warn' : ''}`}>
                  <span className="t-locked-dot" aria-hidden="true" />
                  {recSeconds}s
                </span>
                <button
                  className="t-locked-send"
                  onClick={() => void stopAndTranslate()}
                  aria-label="Send recording"
                >
                  Send ➤
                </button>
              </div>
            ) : (
              <div className="t-mic-dock">
                {/* Both hints live under the button and fade in with travel, so
                    the affordance is discovered by doing rather than by being
                    told about it up front. */}
                <span
                  className="t-gesture-hint t-gesture-hint-cancel"
                  style={{ opacity: phase === 'recording' ? Math.min(1, -dragX / CANCEL_PX) : 0 }}
                  aria-hidden="true"
                >
                  ‹ release to cancel
                </span>
                <span
                  className="t-gesture-hint t-gesture-hint-lock"
                  style={{ opacity: phase === 'recording' ? Math.min(1, dragX / LOCK_PX) : 0 }}
                  aria-hidden="true"
                >
                  lock 🔒 ›
                </span>

                <motion.button
                  className={`t-mic ${phase === 'recording' ? 'rec' : ''} ${
                    dragX < -CANCEL_PX ? 'cancelling' : ''
                  }`}
                  disabled={!sttReady}
                  // Pointer capture keeps every later pointer event on this
                  // element even once the finger has slid well off it, which is
                  // exactly what a drag gesture on a held button needs.
                  style={{ transform: `translateX(${dragX}px)` }}
                  onPointerDown={(e) => {
                    e.currentTarget.setPointerCapture(e.pointerId);
                    dragOriginRef.current = e.clientX;
                    // Synchronously, inside the gesture — Android will not let
                    // us speak later otherwise.
                    tts.unlock();
                    void startRecording();
                  }}
                  onPointerMove={(e) => {
                    if (!recordingRef.current || locked) return;
                    const dx = e.clientX - dragOriginRef.current;
                    // Rubber-band past each threshold rather than tracking the
                    // finger one-to-one: the button visibly stops giving, which
                    // is how you feel a threshold without looking at it.
                    setDragX(dx > 0 ? Math.min(dx, LOCK_PX + 16) : Math.max(dx, -CANCEL_PX - 16));
                    if (dx > LOCK_PX) {
                      setLocked(true);
                      setDragX(0);
                    }
                  }}
                  onPointerUp={(e) => {
                    if (locked) return;
                    const dx = e.clientX - dragOriginRef.current;
                    if (dx < -CANCEL_PX) void discardRecording(null);
                    else void stopAndTranslate();
                  }}
                  // A cancelled pointer is the OS taking the gesture away — a
                  // call, a notification, a palm. Never translate on that: it
                  // was not a decision to send.
                  onPointerCancel={() => void discardRecording(null)}
                  whileTap={reduce ? undefined : { scale: 0.95 }}
                  aria-label={phase === 'recording' ? 'Release to translate' : 'Hold to speak'}
                >
                  {phase === 'recording' && !reduce && (
                    <motion.span
                      className="t-mic-ring"
                      aria-hidden="true"
                      animate={{ scale: [1, 1.35], opacity: [0.55, 0] }}
                      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
                    />
                  )}
                  <MicIcon active={phase === 'recording'} />
                </motion.button>
              </div>
            )}

            <p className={`t-mic-label ${nearLimit ? 'warn' : ''}`}>
              {!sttReady
                ? sttProgress !== null
                  ? `Setting up speech, ${Math.round(sttProgress.percent)}%`
                  : 'Setting up speech'
                : locked
                ? 'Listening. Tap Send when you are done.'
                : phase === 'recording'
                ? `${recSeconds}s. Let go to translate, or slide right to lock.`
                : 'Hold to speak, or slide right to lock'}
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="text-input"
            className="t-input"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -12 }}
            transition={{ duration: 0.18 }}
          >
            <textarea
              className="t-textarea"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={from === 'en' ? 'Type in English…' : 'Введите текст…'}
              lang={from}
              rows={3}
            />
            <motion.button
              className="t-translate"
              disabled={!mtReady || typed.trim() === ''}
              onClick={() => {
                tts.unlock();
                void translateText(typed);
              }}
              whileTap={reduce ? undefined : { scale: 0.97 }}
            >
              {mtReady ? 'Translate' : 'Loading translation…'}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* There is one speech model now, so this is a readout and not a control.
          The picker existed to make the tiny/base choice visible after it had
          been buried in Diagnostics; tiny lost on the evidence, and a chooser
          with one option is just a button that cannot do anything. */}
      {/* The model name, its size and the backend it runs on all used to be
          printed here. None of it told anyone whether the app was working, and
          the one thing people do want to know about a translator on a trip is
          whether it needs signal. So it says that instead. */}
      <footer className="t-footer">
        <p className="t-footer-note">
          Everything happens on this phone. No internet needed, and nothing you
          say is sent anywhere.
        </p>
      </footer>
    </div>
  );
}

function MicIcon({ active }: { active: boolean }) {
  return (
    <svg
      className="t-mic-svg"
      viewBox="0 0 24 24"
      width="42"
      height="42"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="2" width="6" height="12" rx="3" fill={active ? 'currentColor' : 'none'} />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}
